import {createContext, useCallback, useMemo, useState, useEffect, useContext, useRef} from "react";
import { TradingContext } from './TradingContext';

const defaultUseStreams = {
    currentOrders: [],
}

export const StreamsContext = createContext(defaultUseStreams);

export const useStreamsContext = () => {
    const { selectedAccount, positionsManager } = useContext(TradingContext);
    // Keyed bars storage to support multiple bar streams in future: key = symbol|interval|unit|sessionTemplate
    const [barsByKey, setBarsByKey] = useState({});
    /** Per bar-stream key: loading | ready | error — so charts stop spinning on API failure (e.g. not entitled) */
    const [barsStreamMetaByKey, setBarsStreamMetaByKey] = useState({});
    /** Option chain rows keyed by underlying|expiration|strikeProximity */
    const [optionChainsByKey, setOptionChainsByKey] = useState({});
    const [currentBalance, setCurrentBalance] = useState(null)
    // Quotes keyed by symbol
    const [quotesBySymbol, setQuotesBySymbol] = useState({});

    
    const [currentOrders, setCurrentOrders] = useState([]);
    const [barStreamHealthy, setBarStreamHealthy] = useState(false);
    const [quoteStreamHealthy, setQuoteStreamHealthy] = useState(false);
    const [positionsStreamHealthy, setPositionsStreamHealthy] = useState(false);
    const [ordersStreamHealthy, setOrdersStreamHealthy] = useState(false);
    // marketAggregates is now keyed by ticker: { [ticker]: { bids: [], asks: [] } }
    const [marketAggregates, setMarketAggregates] = useState({});
    const [liquidityStreamHealthy, setLiquidityStreamHealthy] = useState(false);
    // Bump this to force all hooks that depend on it to restart streams
    const [streamEpoch, setStreamEpoch] = useState(0);
    // Track last stream initialization time
    const lastStreamInitTimeRef = useRef(Date.now());
    
    // Staggered initialization - delays stream starts to prevent all hitting server at once
    const [staggeredReady, setStaggeredReady] = useState({
        positions: false,
        orders: false,
        quotes: false,
        bars: false,
        liquidity: false
    });
    
    // Track if we're in a manual restart (focus/visibility/manual trigger)
    // This suppresses auto-retry to prevent cascades during intentional restarts
    const manualRestartInProgressRef = useRef(false);
    
    // Track when we last triggered a restart to add cooldown period
    const lastRestartTimeRef = useRef(0);
    const RESTART_COOLDOWN_MS = 15000; // 15 seconds - don't restart again within this window
    
    // Track previous health states to detect healthy -> unhealthy transitions
    const prevBarStreamHealthyRef = useRef(false);
    const prevQuoteStreamHealthyRef = useRef(false);
    const prevPositionsStreamHealthyRef = useRef(false);
    const prevOrdersStreamHealthyRef = useRef(false);
    const prevLiquidityStreamHealthyRef = useRef(false);
    
    // Track retry attempts for each stream (to ensure we only retry once per unhealthy transition)
    const barStreamRetryAttemptedRef = useRef(false);
    const quoteStreamRetryAttemptedRef = useRef(false);
    const positionsStreamRetryAttemptedRef = useRef(false);
    const ordersStreamRetryAttemptedRef = useRef(false);
    const liquidityStreamRetryAttemptedRef = useRef(false);
    
    // Track when streams first became unhealthy (to retry streams that start unhealthy)
    const ordersStreamUnhealthySinceRef = useRef(null);
    const positionsStreamUnhealthySinceRef = useRef(null);
    
    // Track if we're in initial load phase (to avoid retrying streams that start unhealthy)
    const initialLoadPhaseRef = useRef(true);
    const INITIAL_LOAD_DURATION = 10000; // 10 seconds - enough time for streams to initialize
    
    // Mark initial load phase as complete after duration
    useEffect(() => {
        const timer = setTimeout(() => {
            initialLoadPhaseRef.current = false;
        }, INITIAL_LOAD_DURATION);
        return () => clearTimeout(timer);
    }, []);
    
    // Reset retry flags when streams become healthy (for positions/orders only - they use global restart)
    useEffect(() => {
        if (positionsStreamHealthy) {
            positionsStreamRetryAttemptedRef.current = false;
            positionsStreamUnhealthySinceRef.current = null;
        }
    }, [positionsStreamHealthy]);
    
    useEffect(() => {
        if (ordersStreamHealthy) {
            ordersStreamRetryAttemptedRef.current = false;
            ordersStreamUnhealthySinceRef.current = null;
        }
    }, [ordersStreamHealthy]);
    
    // Auto-retry logic: detect healthy -> unhealthy transitions and retry once
    // IMPORTANT: Only positions and orders streams should trigger global epoch restarts
    // Bar, quote, and liquidity streams restart automatically when their URL parameters change (ticker, symbols, etc.)
    // so they should NOT trigger streamEpoch changes that would restart unrelated streams
    
    // Bar stream - just track health, no global restart (restarts on URL change)
    useEffect(() => {
        prevBarStreamHealthyRef.current = barStreamHealthy;
        if (barStreamHealthy) {
            barStreamRetryAttemptedRef.current = false;
        }
    }, [barStreamHealthy]);
    
    // Quote stream - just track health, no global restart (restarts on URL change)
    useEffect(() => {
        prevQuoteStreamHealthyRef.current = quoteStreamHealthy;
        if (quoteStreamHealthy) {
            quoteStreamRetryAttemptedRef.current = false;
        }
    }, [quoteStreamHealthy]);
    
    // Positions stream - trigger global restart since it doesn't change based on ticker
    useEffect(() => {
        const wasHealthy = prevPositionsStreamHealthyRef.current;
        const isUnhealthy = !positionsStreamHealthy;
        const now = Date.now();
        const timeSinceLastRestart = now - lastRestartTimeRef.current;
        
        // Track when stream first became unhealthy
        if (isUnhealthy && positionsStreamUnhealthySinceRef.current === null) {
            positionsStreamUnhealthySinceRef.current = now;
        }
        
        // Retry if stream transitioned from healthy to unhealthy
        if (wasHealthy && isUnhealthy && !positionsStreamRetryAttemptedRef.current && !initialLoadPhaseRef.current && !manualRestartInProgressRef.current && timeSinceLastRestart >= RESTART_COOLDOWN_MS) {
            positionsStreamRetryAttemptedRef.current = true;
            lastRestartTimeRef.current = now;
            // Delay restart by 500ms to avoid rapid reconnection attempts
            setTimeout(() => {
                setStreamEpoch(prev => prev + 1);
            }, 500);
        }
        
        // Also retry if stream started unhealthy and has been unhealthy for a while (after initial load phase)
        const UNHEALTHY_RETRY_DELAY = 8000; // 8 seconds - retry streams that start unhealthy
        if (!wasHealthy && isUnhealthy && positionsStreamUnhealthySinceRef.current !== null && 
            !positionsStreamRetryAttemptedRef.current && !initialLoadPhaseRef.current && 
            !manualRestartInProgressRef.current && timeSinceLastRestart >= RESTART_COOLDOWN_MS) {
            const timeUnhealthy = now - positionsStreamUnhealthySinceRef.current;
            if (timeUnhealthy >= UNHEALTHY_RETRY_DELAY) {
                positionsStreamRetryAttemptedRef.current = true;
                lastRestartTimeRef.current = now;
                setTimeout(() => {
                    setStreamEpoch(prev => prev + 1);
                }, 500);
            }
        }
        
        prevPositionsStreamHealthyRef.current = positionsStreamHealthy;
    }, [positionsStreamHealthy]);
    
    // Orders stream - trigger global restart since it doesn't change based on ticker
    useEffect(() => {
        const wasHealthy = prevOrdersStreamHealthyRef.current;
        const isUnhealthy = !ordersStreamHealthy;
        const now = Date.now();
        const timeSinceLastRestart = now - lastRestartTimeRef.current;
        
        // Track when stream first became unhealthy
        if (isUnhealthy && ordersStreamUnhealthySinceRef.current === null) {
            ordersStreamUnhealthySinceRef.current = now;
        }
        
        // Retry if stream transitioned from healthy to unhealthy
        if (wasHealthy && isUnhealthy && !ordersStreamRetryAttemptedRef.current && !initialLoadPhaseRef.current && !manualRestartInProgressRef.current && timeSinceLastRestart >= RESTART_COOLDOWN_MS) {
            ordersStreamRetryAttemptedRef.current = true;
            lastRestartTimeRef.current = now;
            // Delay restart by 500ms to avoid rapid reconnection attempts
            setTimeout(() => {
                setStreamEpoch(prev => prev + 1);
            }, 500);
        }
        
        // Also retry if stream started unhealthy and has been unhealthy for a while (after initial load phase)
        const UNHEALTHY_RETRY_DELAY = 8000; // 8 seconds - retry streams that start unhealthy
        if (!wasHealthy && isUnhealthy && ordersStreamUnhealthySinceRef.current !== null && 
            !ordersStreamRetryAttemptedRef.current && !initialLoadPhaseRef.current && 
            !manualRestartInProgressRef.current && timeSinceLastRestart >= RESTART_COOLDOWN_MS) {
            const timeUnhealthy = now - ordersStreamUnhealthySinceRef.current;
            if (timeUnhealthy >= UNHEALTHY_RETRY_DELAY) {
                ordersStreamRetryAttemptedRef.current = true;
                lastRestartTimeRef.current = now;
                setTimeout(() => {
                    setStreamEpoch(prev => prev + 1);
                }, 500);
            }
        }
        
        prevOrdersStreamHealthyRef.current = ordersStreamHealthy;
    }, [ordersStreamHealthy]);
    
    // Liquidity stream - just track health, no global restart (restarts on URL change)
    useEffect(() => {
        prevLiquidityStreamHealthyRef.current = liquidityStreamHealthy;
        if (liquidityStreamHealthy) {
            liquidityStreamRetryAttemptedRef.current = false;
        }
    }, [liquidityStreamHealthy]);
    
    // NOTE: Retry flags are reset individually when each stream becomes healthy (lines 59-87)
    // We do NOT reset all flags when streamEpoch changes to prevent cascade retries
    // where one unhealthy stream triggers epoch change, which resets all flags,
    // allowing other streams to retry immediately and create a retry loop

    /**
     * Centralized handler for account changes in StreamsContext
     * Resets all stream-related data when account changes
     * Note: TradingContext also clears orders in setSelectedAccountPersist
     * 
     * IMPORTANT: Skip clearing on initial load to prevent race condition where
     * streams start loading data, then account loads and clears everything
     */
    const prevAccountIdRef = useRef(null);
    const prevMarketDataKeyRef = useRef(null);
    useEffect(() => {
        const currentAccountId = selectedAccount?.AccountID;
        const prevAccountId = prevAccountIdRef.current;
        const marketDataKey = [
            selectedAccount?.adapter_type || '',
            selectedAccount?.ct_connection_id || selectedAccount?.connection_id || '',
        ].join('|');
        
        // Skip on initial mount (when prevAccountId is null and we're setting first account)
        if (prevAccountId === null && currentAccountId) {
            prevAccountIdRef.current = currentAccountId;
            prevMarketDataKeyRef.current = marketDataKey;
            return;
        }
        
        // Only clear when actually switching between accounts (both prev and current are non-null and different)
        if (prevAccountId && currentAccountId && prevAccountId !== currentAccountId) {
            setCurrentBalance(null);
            setCurrentOrders([]);
            // Positions already cleared in TradingContext; clear again so stream handlers can't resurrect old rows
            positionsManager?.clearPositions();
            setPositionsStreamHealthy(false);
            setOrdersStreamHealthy(false);
            setLiquidityStreamHealthy(false);
            
            // CRITICAL: Clear all cached stream data to prevent stale data from appearing
            //setBarsByKey({});
            //setQuotesBySymbol({});
            //setMarketAggregates({});
            // Bars and quotes follow the broker connection, not the account. Switching
            // accounts on the same connection must not mark those streams down or
            // restart them — that drops the trade tape the live candle needs.
            const marketDataChanged = prevMarketDataKeyRef.current !== marketDataKey;
            if (marketDataChanged) {
                setBarStreamHealthy(false);
                setQuoteStreamHealthy(false);
                prevBarStreamHealthyRef.current = false;
                prevQuoteStreamHealthyRef.current = false;
            }
            
            // Reset retry flags and previous health states for clean slate
            barStreamRetryAttemptedRef.current = false;
            quoteStreamRetryAttemptedRef.current = false;
            positionsStreamRetryAttemptedRef.current = false;
            ordersStreamRetryAttemptedRef.current = false;
            liquidityStreamRetryAttemptedRef.current = false;
            
            // Reset unhealthy tracking timestamps
            positionsStreamUnhealthySinceRef.current = null;
            ordersStreamUnhealthySinceRef.current = null;
            
            prevPositionsStreamHealthyRef.current = false;
            prevOrdersStreamHealthyRef.current = false;
            prevLiquidityStreamHealthyRef.current = false;
            
            // Restart account-scoped streams. Leave bar/quote streams up when the
            // market-data connection did not change.
            if (marketDataChanged) {
                setTimeout(() => {
                    setStreamEpoch(prev => prev + 1);
                }, 200);
            }
        }
        
        // Update ref for next comparison
        prevAccountIdRef.current = currentAccountId;
        if (currentAccountId) prevMarketDataKeyRef.current = marketDataKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount?.AccountID]);

    // Track when streams are initialized and apply staggered delays
    useEffect(() => {
        const now = Date.now();
        lastStreamInitTimeRef.current = now;
        lastRestartTimeRef.current = now; // Update restart time when epoch changes
        // Reset unhealthy timestamps on restart to track fresh connection attempts
        positionsStreamUnhealthySinceRef.current = null;
        ordersStreamUnhealthySinceRef.current = null;
        // Suppress auto-retry during restart grace period
        manualRestartInProgressRef.current = true;
        setTimeout(() => {
            manualRestartInProgressRef.current = false;
        }, 5000); // 5 second grace period after restart
        
        // Reset staggered ready states
        setStaggeredReady({
            positions: false,
            orders: false,
            quotes: false,
            bars: false,
            liquidity: false
        });
        
        // Mark all streams ready immediately (no stagger)
        setStaggeredReady({
            positions: true,
            orders: true,
            quotes: true,
            bars: true,
            liquidity: true
        });
    }, [streamEpoch]);

    // Periodic check for streams that started unhealthy (complements the transition-based retry)
    useEffect(() => {
        const checkInterval = setInterval(() => {
            const now = Date.now();
            const timeSinceLastRestart = now - lastRestartTimeRef.current;
            
            // Skip if in cooldown or manual restart
            if (timeSinceLastRestart < RESTART_COOLDOWN_MS || manualRestartInProgressRef.current || initialLoadPhaseRef.current) {
                return;
            }
            
            const UNHEALTHY_RETRY_DELAY = 8000; // 8 seconds
            
            // Check orders stream
            if (!ordersStreamHealthy && ordersStreamUnhealthySinceRef.current !== null && !ordersStreamRetryAttemptedRef.current) {
                const timeUnhealthy = now - ordersStreamUnhealthySinceRef.current;
                if (timeUnhealthy >= UNHEALTHY_RETRY_DELAY) {
                    ordersStreamRetryAttemptedRef.current = true;
                    lastRestartTimeRef.current = now;
                    setTimeout(() => {
                        setStreamEpoch(prev => prev + 1);
                    }, 500);
                }
            }
            
            // Check positions stream
            if (!positionsStreamHealthy && positionsStreamUnhealthySinceRef.current !== null && !positionsStreamRetryAttemptedRef.current) {
                const timeUnhealthy = now - positionsStreamUnhealthySinceRef.current;
                if (timeUnhealthy >= UNHEALTHY_RETRY_DELAY) {
                    positionsStreamRetryAttemptedRef.current = true;
                    lastRestartTimeRef.current = now;
                    setTimeout(() => {
                        setStreamEpoch(prev => prev + 1);
                    }, 500);
                }
            }
        }, 2000); // Check every 2 seconds for faster retry
        
        return () => clearInterval(checkInterval);
    }, [ordersStreamHealthy, positionsStreamHealthy]);
    
    // Health monitoring watchdog: auto-restart streams that stay unhealthy for too long
    useEffect(() => {
        const healthCheckInterval = setInterval(() => {
            const now = Date.now();
            const timeSinceLastInit = now - lastStreamInitTimeRef.current;
            const timeSinceLastRestart = now - lastRestartTimeRef.current;
            
            // Don't check health too soon after initialization (give streams time to connect)
            if (timeSinceLastInit < 8000) {
                return;
            }
            
            // Don't restart if we're in cooldown period or manual restart
            if (timeSinceLastRestart < RESTART_COOLDOWN_MS || manualRestartInProgressRef.current) {
                return;
            }
            
            // Check if any critical stream has been unhealthy
            const unhealthyStreams = [];
            
            // Only check positions and orders streams for global restart
            // (bar and quote streams auto-restart on URL changes)
            if (!positionsStreamHealthy) {
                unhealthyStreams.push('positions');
            }
            if (!ordersStreamHealthy) {
                unhealthyStreams.push('orders');
            }
            
            // If streams are unhealthy and we haven't recently restarted, trigger restart
            // Require at least 20 seconds since last init to avoid restarting during normal initialization
            if (unhealthyStreams.length > 0 && timeSinceLastInit > 20000) {
                console.warn(`🔄 [StreamsContext] Health watchdog: Restarting unhealthy streams: ${unhealthyStreams.join(', ')}`);
                
                // Reset retry flags to allow another retry attempt
                positionsStreamRetryAttemptedRef.current = false;
                ordersStreamRetryAttemptedRef.current = false;
                lastRestartTimeRef.current = now;
                
                setStreamEpoch(prev => prev + 1);
            }
        }, 10000); // Check every 10 seconds
        
        return () => clearInterval(healthCheckInterval);
    }, [positionsStreamHealthy, ordersStreamHealthy]);

    // Refs to track current health states (read at event time, not captured in closure)
    const barStreamHealthyRef = useRef(barStreamHealthy);
    const quoteStreamHealthyRef = useRef(quoteStreamHealthy);
    const positionsStreamHealthyRef = useRef(positionsStreamHealthy);
    const ordersStreamHealthyRef = useRef(ordersStreamHealthy);
    
    // Keep refs in sync with state
    useEffect(() => {
        barStreamHealthyRef.current = barStreamHealthy;
    }, [barStreamHealthy]);
    
    useEffect(() => {
        quoteStreamHealthyRef.current = quoteStreamHealthy;
    }, [quoteStreamHealthy]);
    
    useEffect(() => {
        positionsStreamHealthyRef.current = positionsStreamHealthy;
    }, [positionsStreamHealthy]);
    
    useEffect(() => {
        ordersStreamHealthyRef.current = ordersStreamHealthy;
    }, [ordersStreamHealthy]);
    
    // Restart streams on focus/visibility regain with intelligent conditions
    // IMPORTANT: No health dependencies to prevent re-creating listeners on health changes
    useEffect(() => {
        let restartTimer = null;
        let clearManualRestartTimer = null;
        
        const scheduleRestart = () => {
            // Clear any pending restart
            if (restartTimer) {
                clearTimeout(restartTimer);
            }
            // Debounce stream restarts by 300ms to prevent rapid reconnections
            restartTimer = setTimeout(() => {
                // Check if streams need to be restarted
                const timeSinceInit = Date.now() - lastStreamInitTimeRef.current;
                const FIVE_MINUTES = 1 * 60 * 1000; // 1 minutes in milliseconds
                
                // Check if any stream is unhealthy (read from refs to get latest values)
                const anyStreamUnhealthy = !barStreamHealthyRef.current || 
                                          !quoteStreamHealthyRef.current || 
                                          !positionsStreamHealthyRef.current || 
                                          !ordersStreamHealthyRef.current;
                
                // Only restart if streams are unhealthy OR it's been 5+ minutes
                if (anyStreamUnhealthy || timeSinceInit >= FIVE_MINUTES) {
                    // Set manual restart flag to suppress auto-retry during restart
                    manualRestartInProgressRef.current = true;
                    
                    // Trigger restart
                    setStreamEpoch(prev => prev + 1);
                    
                    // Clear manual restart flag after 2 seconds (enough time for streams to stabilize)
                    if (clearManualRestartTimer) {
                        clearTimeout(clearManualRestartTimer);
                    }
                    clearManualRestartTimer = setTimeout(() => {
                        manualRestartInProgressRef.current = false;
                    }, 2000);
                }
                
                restartTimer = null;
            }, 300);
        };
        
        const onFocus = () => scheduleRestart();
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                scheduleRestart();
            }
        };
        
        try { window.addEventListener('focus', onFocus); } catch (_) {}
        try { document.addEventListener('visibilitychange', onVisibility); } catch (_) {}
        return () => {
            if (restartTimer) {
                clearTimeout(restartTimer);
            }
            if (clearManualRestartTimer) {
                clearTimeout(clearManualRestartTimer);
            }
            try { window.removeEventListener('focus', onFocus); } catch (_) {}
            try { document.removeEventListener('visibilitychange', onVisibility); } catch (_) {}
        };
    }, []); // Empty deps - set up listeners once, read health from refs

    const openStopLossOrders = useMemo(() => {
        // TS API OrderType enum: "Market" | "Limit" | "StopMarket" | "StopLimit"
        return currentOrders.filter((order) => {
            const type = order?.OrderType;
            return type === 'StopMarket' || type === 'StopLimit';
        });
    }, [currentOrders]);

    // Memoize restartStreams function to keep it stable
    const restartStreams = useCallback(() => {
        // Set manual restart flag to prevent cascade
        manualRestartInProgressRef.current = true;
        lastRestartTimeRef.current = Date.now();
        setStreamEpoch(prev => prev + 1);
        
        // Clear flag after 5 seconds (longer grace period for manual restarts)
        setTimeout(() => {
            manualRestartInProgressRef.current = false;
        }, 5000);
    }, []);

    // Memoize context value to prevent unnecessary re-renders
    return useMemo(() => ({
        // Balance
        currentBalance,
        setCurrentBalance,
        // Bar
        barsByKey,
        setBarsByKey,
        barsStreamMetaByKey,
        setBarsStreamMetaByKey,
        optionChainsByKey,
        setOptionChainsByKey,
        // Quote
        quotesBySymbol,
        setQuotesBySymbol,
        // Orders
        currentOrders,
        setCurrentOrders,
        openStopLossOrders,
        // Orders Stream
        ordersStreamHealthy,
        setOrdersStreamHealthy,
        // Bar Stream
        barStreamHealthy,
        setBarStreamHealthy,
        // Quote Stream
        quoteStreamHealthy,
        setQuoteStreamHealthy,
        // Position Stream
        positionsStreamHealthy,
        setPositionsStreamHealthy,
        // Market Aggregates
        marketAggregates,
        setMarketAggregates,
        liquidityStreamHealthy,
        setLiquidityStreamHealthy,
        // Stream restart controls
        streamEpoch,
        restartStreams,
        // Staggered initialization flags - use these to delay stream starts
        staggeredReady,
    }), [
        currentBalance,
        setCurrentBalance,
        barsByKey,
        setBarsByKey,
        barsStreamMetaByKey,
        setBarsStreamMetaByKey,
        optionChainsByKey,
        setOptionChainsByKey,
        quotesBySymbol,
        setQuotesBySymbol,
        currentOrders,
        setCurrentOrders,
        openStopLossOrders,
        ordersStreamHealthy,
        setOrdersStreamHealthy,
        barStreamHealthy,
        setBarStreamHealthy,
        quoteStreamHealthy,
        setQuoteStreamHealthy,
        positionsStreamHealthy,
        setPositionsStreamHealthy,
        marketAggregates,
        setMarketAggregates,
        liquidityStreamHealthy,
        setLiquidityStreamHealthy,
        streamEpoch,
        restartStreams,
        staggeredReady,
    ]);
};

export const StreamsContextProvider = ({ children }) => {
    return <StreamsContext.Provider value={useStreamsContext()}>
        {children}
    </StreamsContext.Provider>
};